import { Button, ButtonLink, Flex, Text } from '@applyboard/crystal-ui'
import { IntakeSearchResource } from '@backend/resources/intake-search/types'

import { StudentApplication } from '../../../../components/Application/types'
import { ApplicationState } from '../../../../hooks'
import { ChangeProgramDialog } from '../../ChangeProgramDialog'

function NotClickableLabel(props: Readonly<{ label: string }>) {
  return (
    <Flex pb={5} justify="center">
      <Text variant="labelL" contrast="high">
        {props.label}
      </Text>
    </Flex>
  )
}

function ActionButton(
  props: Readonly<{
    text: string
    onClick: () => void
    emphasis: 'highlighted' | 'outlined'
    loading?: boolean
    disabled?: boolean
  }>,
) {
  return (
    <Button
      intent="primary"
      emphasis={props.emphasis}
      width="fill"
      onClick={props.onClick}
      disabled={props.disabled}
      loading={props.loading}
    >
      {props.text}
    </Button>
  )
}

function ViewApplicationLink(
  props: Readonly<{
    href: string
    emphasis: 'highlighted' | 'outlined'
    loading?: boolean
    disabled?: boolean
  }>,
) {
  return (
    <ButtonLink
      intent="primary"
      emphasis={props.emphasis}
      width="fill"
      href={props.href}
      disabled={props.disabled}
      loading={props.loading}
    >
      View Application
    </ButtonLink>
  )
}

type ProgramCardButtonProps = Readonly<{
  loading?: boolean
  disabled?: boolean
  onClick: () => void
  intakeSearch: IntakeSearchResource
  currentApplication?: StudentApplication['attributes']
  primarySelectedProgramId?: string
  alternateProgramSelected?: string
  buttonEmphasis?: 'highlighted' | 'outlined'
}>

export function ProgramCardButton({
  buttonEmphasis = 'outlined',
  ...props
}: ProgramCardButtonProps) {
  if (props.intakeSearch.attributes.intake.status !== 'OPEN') {
    return <NotClickableLabel label="Not Available" />
  }

  if (props.primarySelectedProgramId === props.intakeSearch.id) {
    return <NotClickableLabel label="Selected Primary" />
  }

  if (props.alternateProgramSelected === props.intakeSearch.id) {
    return <NotClickableLabel label="Selected Alternate" />
  }

  if (
    props.currentApplication &&
    props.currentApplication.programSelected?.programIntakeId === props.intakeSearch.id
  ) {
    return props.currentApplication.alternateProgramSelected?.length ? (
      <NotClickableLabel label="Currently Selected" />
    ) : (
      <ActionButton
        text="Confirm selection"
        onClick={props.onClick}
        emphasis={buttonEmphasis}
        loading={props.loading}
        disabled={props.disabled}
      />
    )
  }

  const activeApplication = props.intakeSearch.attributes.applications?.find(
    app =>
      app.applicationState !== ApplicationState.DECLINED &&
      app.applicationState !== ApplicationState.INITIAL_OFFER,
  )

  if (activeApplication) {
    const href =
      activeApplication.applicationState === ApplicationState.DRAFT
        ? `/applications/new?applicationId=${activeApplication.id}`
        : `/applications/${activeApplication.id}`
    return (
      <ViewApplicationLink
        href={href}
        emphasis={buttonEmphasis}
        loading={props.loading}
        disabled={props.disabled}
      />
    )
  }

  if (props.currentApplication && !props.primarySelectedProgramId) {
    return (
      <ChangeProgramDialog
        onConfirm={props.onClick}
        loading={props.loading}
        disabled={props.disabled}
        emphasis={buttonEmphasis}
        currentApplication={props.currentApplication}
        selectedIntake={props.intakeSearch}
      />
    )
  }

  const buttonText = props.primarySelectedProgramId
    ? props.currentApplication?.alternateProgramSelected?.length
      ? 'Update alternate'
      : 'Add alternate'
    : 'Apply'

  return (
    <ActionButton
      text={buttonText}
      onClick={props.onClick}
      emphasis={buttonEmphasis}
      loading={props.loading}
      disabled={props.disabled}
    />
  )
}
